import React from 'react';
import PropTypes from 'prop-types';

import styles from './Hero.module.postcss';
import ResponsiveImg from '../../shared/ResponsiveImg';

// Local, origin-served, hashed variants of the default home hero. PR3 first
// shipped resized WebP variants (298 KB → 53 KB mobile LCP); PR5 (Apr 8 2026)
// re-encoded from the original Unsplash master at 5943×3962 and added AVIF
// primary sources so the <picture> element serves AVIF to every browser in
// our tightened browserslist (last-2 Chrome/FF/Safari/iOS + Samsung 14+ +
// Firefox ESR) and falls back to WebP otherwise.
//
// Final sizes (all center-cropped to 2:1, mastered at cwebp/AOM effort 6):
//   mobile  800×400  AVIF q=40   29.6 KiB  ← primary LCP payload
//           800×400  WebP q=50   44.0 KiB  ← fallback
//   desktop 1600×800 AVIF q=40   68.5 KiB
//           1600×800 WebP q=50  112.9 KiB
//
// PSNR against the uncompressed source PNG: AVIF 800w = 29.23 dB, WebP 800w =
// 29.45 dB (the previous 51.5 KiB WebP baseline was 29.71 dB — so AVIF is a
// 42% size drop for a 0.48 dB perceptual loss, imperceptible on mobile). On
// the 1600w variant AVIF is actually +2.7 dB BETTER than the old WebP while
// being 52% smaller, because the original PR3 encoder settings were too
// conservative for a 1.5 MP file.
//
// Origin-served (`/static/photos/…`) via the Austin nginx droplet which
// emits `Cache-Control: public, max-age=31536000, immutable` for the whole
// `/static/photos/` location (see nginx site config). Same browser-cache
// behavior as the S3 CDN, but deployed via Capistrano instead of AWS CLI.
const MOBILE_DEFAULT_HERO_AVIF = '/static/photos/home-hero-800.avif';
const DESKTOP_DEFAULT_HERO_AVIF = '/static/photos/home-hero-1600.avif';
const MOBILE_DEFAULT_HERO = '/static/photos/home-hero-800.webp';
const DESKTOP_DEFAULT_HERO = '/static/photos/home-hero-1600.webp';

// Heuristic: legacy callers pass the full CDN URL for the default Seattle
// hero. When we see that, swap in the origin-served resized versions. Any
// other `photo` prop (Portland hero, city-specific CMS override, etc.) is
// passed through unchanged so the redesign PR doesn't accidentally regress
// other cities' heroes.
function isDefaultSeattleHero(photo) {
  return typeof photo === 'string' && /\/photos\/home-hero\.webp$/.test(photo);
}

function Hero({ children, photo, isResponsive = false }) {
  const useResized = isDefaultSeattleHero(photo);
  const mobileSrc = useResized ? MOBILE_DEFAULT_HERO : photo;
  const desktopSrc = useResized ? DESKTOP_DEFAULT_HERO : photo;

  return (
    <div className={styles.Hero}>
      {photo && !useResized && isResponsive && (
        <ResponsiveImg
          src={photo}
          widths={[400, 600, 800, 1000]}
          sizes='100vw'
          alt=''
          className={styles.Hero_image}
          width={1600}
          height={800}
          fetchPriority='high'
          loading='eager'
          decoding='async'
        />
      )}
      {photo && (useResized || !isResponsive) && (
        <picture>
          {useResized && (
            <>
              {/* Primary path: AVIF (covered by our browserslist). */}
              <source
                media='(max-width: 768px)'
                srcSet={MOBILE_DEFAULT_HERO_AVIF}
                width='800'
                height='400'
                type='image/avif'
              />
              <source
                media='(min-width: 769px)'
                srcSet={DESKTOP_DEFAULT_HERO_AVIF}
                width='1600'
                height='800'
                type='image/avif'
              />
              {/* Fallback path: WebP mobile. Desktop WebP falls through to <img>. */}
              <source
                media='(max-width: 768px)'
                srcSet={mobileSrc}
                width='800'
                height='400'
                type='image/webp'
              />
            </>
          )}
          <img
            className={styles.Hero_image}
            src={desktopSrc}
            alt=''
            width={1600}
            height={800}
            fetchPriority='high'
            loading='eager'
            decoding='async'
          />
        </picture>
      )}
      <div className={styles.Hero_content}>
        {children}
      </div>
    </div>
  );
}

Hero.propTypes = {
  children: PropTypes.node,
  photo: PropTypes.string,
  isResponsive: PropTypes.bool,
};

export default Hero;
